import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import parse from 'html-react-parser';
import {CustomTabPanel} from '../TabTechnology';
import {
    contentMaxWidth,
    colors,
    fontSize,
    fontWeight,
    letterSpacing,
    lineHeight,
    spacing,
    borderRadius,
    shadows,
    transition,
    typography
} from '@/styles/tokens';
import Eyebrow from '@/components/Common/Eyebrow';

interface CapabilityItem {
    heading: string;
    body: string;
    [key: string]: any;
}

interface CapabilitiesTab {
    header: string;
    children: CapabilityItem[];
}

interface CapabilitiesTableTabProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    technology: CapabilitiesTab[];
}

// ─── Module-level sx constants ────────────────────────────────────────────────

const sectionHeaderSx = {
    textAlign: 'center',
    maxWidth: contentMaxWidth.standard,
    margin: '0 auto',
    mb: spacing[40]
} as const;

const headingTypoSx = {
    ...typography.sectionHeading,
    mb: spacing[24]
} as const;

const subheadingTypoSx = {
    fontSize: fontSize[16],
    lineHeight: lineHeight[28],
    color: colors.text.primary
} as const;

const tableContainerSx = {
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.lg,
    boxShadow: shadows.lg,
    overflow: 'hidden',
    bgcolor: colors.background.default
} as const;

const tableHeaderRowSx = {
    bgcolor: colors.background.slate50,
    borderBottom: `1px solid ${colors.neutral.borderLight}`,
    display: 'flex',
    gap: spacing[32],
    px: spacing[32],
    py: spacing[20]
} as const;

const tableHeaderTextSx = {
    fontSize: fontSize[12],
    fontWeight: fontWeight.semibold,
    color: colors.text.muted,
    letterSpacing: letterSpacing.mid,
    textTransform: 'uppercase' as const,
    lineHeight: 'normal'
} as const;

const tableRowBodyHeadingSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.semibold,
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

const tableRowBodyTextSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

const headerCapabilityColSx = {
    width: {xs: '40%', md: '30%'},
    flexShrink: 0
} as const;

const headerDescriptionColSx = {
    flex: 1,
    minWidth: 0
} as const;

const rowCapabilityColSx = {
    width: {xs: '100%', sm: '40%', md: '30%'},
    flexShrink: 0
} as const;

const rowDescriptionColSx = {
    flex: 1,
    minWidth: 0
} as const;

// ─── Component ────────────────────────────────────────────────────────────────

const CapabilitiesTableTab = ({eyebrow, heading, subheading, technology}: CapabilitiesTableTabProps) => {
    const [value, setValue] = useState(0);

    return (
        <Box>
            <Box sx={sectionHeaderSx}>
                {eyebrow && <Eyebrow text={eyebrow} />}
                <Typography variant="h1" component="h2" className="font-headers" sx={headingTypoSx}>
                    {heading ? parse(heading) : ''}
                </Typography>
                {subheading && (
                    <Typography
                        variant="subtitle1"
                        component="p"
                        className="font-regular-text generative-transforming-subheading"
                        sx={subheadingTypoSx}
                    >
                        {parse(subheading)}
                    </Typography>
                )}
            </Box>

            <Box display="flex" justifyContent="center" gap={spacing[8]} flexWrap="wrap" mb={spacing[32]}>
                {technology?.map((item, index) => {
                    const isActive = value === index;
                    return (
                        <Box
                            key={index}
                            onClick={() => setValue(index)}
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 1,
                                px: '25px',
                                borderRadius: borderRadius.lg,
                                cursor: 'pointer',
                                border: `2px solid ${colors.primary.deep}`,
                                fontWeight: fontWeight.bold,
                                fontSize: fontSize[16],
                                lineHeight: lineHeight[28],
                                letterSpacing: letterSpacing.snug,
                                transition: `all ${transition.slow}`,
                                bgcolor: isActive ? colors.primary.deep : colors.background.default,
                                color: isActive ? colors.text.inverse : colors.primary.deep,
                                boxShadow: isActive ? shadows.awardCardElevated : shadows.sm,
                                whiteSpace: 'nowrap',
                                userSelect: 'none',
                                '&:hover': {
                                    bgcolor: isActive ? colors.primary.deep : colors.background.subtle
                                }
                            }}
                        >
                            {item.header}
                        </Box>
                    );
                })}
            </Box>

            {technology?.map((tabData, index) => (
                <CustomTabPanel value={value} index={index} key={index}>
                    <Box sx={tableContainerSx}>
                        <Box sx={tableHeaderRowSx}>
                            <Box sx={headerCapabilityColSx}>
                                <Typography sx={tableHeaderTextSx} className="font-headers">
                                    Capability
                                </Typography>
                            </Box>
                            <Box sx={headerDescriptionColSx}>
                                <Typography sx={tableHeaderTextSx} className="font-headers">
                                    Description
                                </Typography>
                            </Box>
                        </Box>

                        {tabData.children?.map((item, itemIndex) => {
                            const isLast = itemIndex === tabData.children.length - 1;
                            return (
                                <Box
                                    key={itemIndex}
                                    sx={{
                                        display: 'flex',
                                        gap: spacing[32],
                                        px: spacing[32],
                                        py: spacing[24],
                                        borderBottom: isLast ? 'none' : `1px solid ${colors.neutral.borderLight}`,
                                        flexDirection: {xs: 'column', sm: 'row'}
                                    }}
                                >
                                    <Box sx={rowCapabilityColSx}>
                                        <Typography sx={tableRowBodyHeadingSx} className="font-headers">
                                            {item.heading}
                                        </Typography>
                                    </Box>
                                    <Box sx={rowDescriptionColSx}>
                                        <Typography sx={tableRowBodyTextSx} className="font-regular-text ">
                                            {item.body ? parse(item.body) : ''}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </CustomTabPanel>
            ))}
        </Box>
    );
};

export default memo(CapabilitiesTableTab);
